import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { decideEvent, REALTIME_TIMINGS, type ConnectionState, type SlotsChangedEvent } from './stay-sync';

export interface StayEventHandlers {
  /** Current version known by the screen (from the last board read or applied event). */
  getVersion(): number;
  /** An in-order slot change: apply it locally. */
  onSlotsChanged(event: SlotsChangedEvent): void;
  /** Anything else (gap, structural change, reconnection, polling tick): re-read the board. */
  onResync(): void;
  /** The viewer's own swap requests changed (asked, answered, expired, invalidated): re-read that list. */
  onSwapsChanged?(): void;
  /** The server ended the stream for this session (revoked, expired, forbidden). */
  onSessionEnded?(reason: string): void;
}

/**
 * Live updates of a stay over SSE (REALTIME_DESIGN.md §6–§8), shared by every app that shows a board. Commands stay
 * REST; this only keeps the screen fresh and HONEST: it reports the connection state so the UI can say "en vivo",
 * "reconectando" or "actualización cada 10 s" — never silently stale.
 */
export function useStayEvents(
  stayId: string | undefined,
  handlers: StayEventHandlers,
  baseUrl = '/api/v1',
): ConnectionState {
  const [state, setState] = useState<ConnectionState>('connecting');
  // The latest handlers, updated after each render (never during it: react-hooks/refs).
  const handlersRef = useRef(handlers);
  useLayoutEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (stayId === undefined) return undefined;
    let source: EventSource | undefined;
    let failures = 0;
    let lastSeen = Date.now();
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let closedForGood = false;
    let mode: ConnectionState = 'connecting';
    const setMode = (next: ConnectionState): void => {
      mode = next;
      setState(next);
    };
    const h = (): StayEventHandlers => handlersRef.current;

    const seen = (): void => {
      lastSeen = Date.now();
    };
    const versionOf = (event: MessageEvent<string>): number | undefined => {
      try {
        const data = JSON.parse(event.data) as { stayVersion?: unknown };
        return typeof data.stayVersion === 'number' ? data.stayVersion : undefined;
      } catch {
        return undefined;
      }
    };

    const startPolling = (): void => {
      source?.close();
      setMode('polling');
      pollTimer ??= setInterval(() => {
        h().onResync();
      }, REALTIME_TIMINGS.pollingMs);
    };

    const open = (): void => {
      source?.close();
      const es = new EventSource(`${baseUrl}/stays/${stayId}/events`);
      source = es;
      es.addEventListener('open', () => {
        failures = 0;
        seen();
        setMode('live');
      });
      es.addEventListener('error', () => {
        if (closedForGood) return;
        failures += 1;
        setMode('reconnecting');
        if (failures >= REALTIME_TIMINGS.failuresBeforePolling) startPolling();
      });
      es.addEventListener('hello', (event: MessageEvent<string>) => {
        seen();
        const version = versionOf(event);
        if (version !== undefined && version !== h().getVersion()) h().onResync();
      });
      es.addEventListener('slots.changed', (event: MessageEvent<string>) => {
        seen();
        let data: SlotsChangedEvent;
        try {
          data = JSON.parse(event.data) as SlotsChangedEvent;
        } catch {
          h().onResync();
          return;
        }
        const decision = decideEvent(h().getVersion(), data.stayVersion);
        if (decision === 'apply') h().onSlotsChanged(data);
        else if (decision === 'resync') h().onResync();
      });
      for (const type of ['rooms.changed', 'stay.status-changed', 'physical-label.changed', 'resync.required']) {
        es.addEventListener(type, () => {
          seen();
          h().onResync();
        });
      }
      es.addEventListener('swaps.changed', () => {
        seen();
        h().onSwapsChanged?.();
      });
      es.addEventListener('version', (event: MessageEvent<string>) => {
        seen();
        const version = versionOf(event);
        if (version !== undefined && version > h().getVersion()) h().onResync();
      });
      es.addEventListener('session.ended', (event: MessageEvent<string>) => {
        closedForGood = true;
        es.close();
        let reason = 'ENDED';
        try {
          const parsed = (JSON.parse(event.data) as { reason?: unknown }).reason;
          if (typeof parsed === 'string') reason = parsed;
        } catch {
          // keep the default
        }
        h().onSessionEnded?.(reason);
      });
    };

    // Silence watchdog: heartbeats arrive every ≤ 25 s; nothing for 45 s ⇒ reopen and re-read.
    const watchdog = setInterval(() => {
      if (mode === 'polling' || closedForGood) return;
      if (Date.now() - lastSeen > REALTIME_TIMINGS.silenceMs) {
        seen();
        setMode('reconnecting');
        open();
        h().onResync();
      }
    }, 5_000);
    // Back to the foreground (phone woke up, tab shown): re-read immediately.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') h().onResync();
    };
    document.addEventListener('visibilitychange', onVisible);

    open();
    return () => {
      closedForGood = true;
      clearInterval(watchdog);
      if (pollTimer !== undefined) clearInterval(pollTimer);
      document.removeEventListener('visibilitychange', onVisible);
      source?.close();
    };
    // Reopen only when the stay (or base URL) changes; handlers are read through a ref.
  }, [stayId, baseUrl]);

  return state;
}
